/**
 * User validation service for ContraVault Outlook Add-in
 * Handles checking user access and company permissions
 */

export interface ValidationResult {
  success: boolean;
  error?: string;
  message?: string;
  showContactPage?: boolean;
  showAccessDeniedPage?: boolean;
  companyName?: string;
  userEmail?: string;
}

class UserValidationService {
  private baseUrl: string;

  constructor() {
    // Use the API server URL for validation calls
    this.baseUrl = window.location.origin;
    console.log(`[VALIDATION_SERVICE] Initialized with baseUrl: ${this.baseUrl}`);
  }

  /**
   * Validate user access to the Outlook Add-in
   * @param userEmail - The user's email address
   * @returns Promise<ValidationResult>
   */
  async validateUserAccess(userEmail: string): Promise<ValidationResult> {
    console.log(`[VALIDATION_SERVICE] 🚀 Starting validation for: ${userEmail}`);
    
    if (!userEmail) {
      console.log(`[VALIDATION_SERVICE] ❌ No email provided`);
      return {
        success: false,
        error: 'INVALID_EMAIL',
        message: 'Email address is required',
        showContactPage: true
      };
    }

    try {
      const url = `${this.baseUrl}/api/outlook/email?action=validate&email=${encodeURIComponent(userEmail)}`;
      console.log(`[VALIDATION_SERVICE] 📡 Making validation request to: ${url}`);
      
      const response = await fetch(url, {
        method: 'GET',
        headers: {
          'Content-Type': 'application/json',
        },
      });

      console.log(`[VALIDATION_SERVICE] 📡 Response status: ${response.status} ${response.statusText}`);

      if (!response.ok) {
        throw new Error(`HTTP ${response.status}: ${response.statusText}`);
      }

      const result = await response.json();
      console.log(`[VALIDATION_SERVICE] ✅ Validation result:`, result);
      return result;
      
    } catch (error) {
      console.error(`[VALIDATION_SERVICE] ❌ Validation failed for ${userEmail}:`, error);
      
      return {
        success: false,
        error: 'NETWORK_ERROR',
        message: 'Unable to validate user access. Please check your connection and try again.',
        showContactPage: true
      };
    }
  }

  /**
   * Check if the error indicates user should retry
   * @param error - Error code from validation
   * @returns boolean
   */
  isRetryableError(error?: string): boolean {
    return error === 'NETWORK_ERROR' || error === 'VALIDATION_ERROR';
  }

  /**
   * Get user-friendly error message
   * @param error - Error code from validation
   * @returns string
   */
  getUserFriendlyMessage(error?: string): string {
    switch (error) {
      case 'USER_NOT_FOUND':
        return 'Your account was not found in our system. Please contact support to get set up.';
      case 'NO_COMPANY':
        return 'No company is associated with your account. Please contact support for assistance.';
      case 'NO_ACCESS':
        return 'Your company does not have access to this add-in. Please contact support to request access.';
      case 'NETWORK_ERROR':
        return 'Unable to verify your access due to a network issue. Please check your connection and try again.';
      case 'VALIDATION_ERROR':
        return 'An error occurred while checking your access. Please try again or contact support.';
      default:
        return 'An unexpected error occurred. Please contact support for assistance.';
    }
  }

  /**
   * Determine which page to show based on validation result
   * @param result - Validation result from the API
   * @returns 'contact' | 'accessDenied' | 'allowed' | 'loading'
   */
  getPageToShow(result?: ValidationResult): 'contact' | 'accessDenied' | 'allowed' | 'loading' {
    if (!result) {
      return 'loading';
    }

    if (result.success) {
      return 'allowed';
    }

    if (result.showAccessDeniedPage) {
      return 'accessDenied';
    }

    if (result.showContactPage) {
      return 'contact';
    }

    // Default fallback
    return 'contact';
  }
}

export const userValidationService = new UserValidationService();
export default userValidationService;